import { atomWithIndexedPersistence, atomWithPersistence, idbStorageEffect } from "../utils/persistence";
import { FormContent, FormContentIds, FormItem, FormSubmitTemplate, FormTemplate } from "../types/form";
import { atom, atomFamily } from "recoil";
import { selector, selectorFamily } from "./recoilSelectors";
import { companyFieldState } from "./accountState";
import { getMatchingConditional, getSectionsMissingAnswers, isFormItemTypeRequirable, isFormItemValueEmpty } from "../utils/formUtils";
import { Timestamp, Uid } from "../types/core";
import { VoizReportSubmission } from "../types/voizReport";

export const formTemplatesState = atomWithIndexedPersistence("formtemplates", [] as FormTemplate[]);

/** true once the server sent this session's templates; until then the list is empty or from the last visit */
export const formTemplatesLoadedState = atom<boolean>({ key: "formtemplates.loaded", default: false });

export const formTemplatesSortedState = selector({
    key: "formtemplates.sorted",
    get: ({ get }) => {
        const templates = get(formTemplatesState);
        return templates
            .filter(t => true)
            // a template with no title used to throw here and take the whole
            // selector — and every picker reading it — down with it
            .sort((t1, t2) => (t1.content?.title ?? "").localeCompare(t2.content?.title ?? ""));
    }
});

export const recentSubmissionFormsState = atomWithIndexedPersistence("formsubmissions.recent", [] as FormTemplate[]);
export const recentSubmissionFormUidsState = atomWithPersistence<{uid: Uid, timestamp: Timestamp}[]>("formsubmissionUids.recent", [])

export const recentSubmissionFormsSortedState = selector({
    key: "formsubmissions.recent.sorted",
    get: ({ get }) => {
        const forms = get(recentSubmissionFormsState);
        return forms
            .filter(t => true)
            .sort((f1, f2) => {
                return f2.content.timestamp - f1.content.timestamp
            });
    }
});

export const selectedFormState = atom<FormTemplate | undefined>({ key: "form.template.selected", default: undefined });

export const showVoizReportsState = atom<boolean>({ key: "form.voiz.show", default: false });

export const selectedFormSubmitState = atom<FormTemplate | undefined>({ key: "form.submit.selected", default: undefined });

export const formSubmitState = atomFamily<FormSubmitTemplate | undefined, string>({
    key: "form.submit",
    default: undefined,
    effects: [idbStorageEffect],
});

export const formSubmitIsValidState = selectorFamily<boolean, string>({
    key: "form.submit.isValid",
    get: (uid) => ({ get }) => {
        const form = get(formSubmitState(uid));
        if (!form) return false;

        const formContent = form.content;
        // FINDS ANY ITEM WITH INVALID VALUE OR WITH ANY INVALID VALUE IN THEIR CONDITIONALS IF APPLY
        const invalidItemIndex = formContent.content.findIndex((item) => {
            const matchingConditional = getMatchingConditional(item);
            if (matchingConditional) {
                const invalidConditionalItemIndex = matchingConditional.content.findIndex((conditionalItem) => {
                    return isFormItemTypeRequirable(conditionalItem.type)
                        && conditionalItem.required
                        && isFormItemValueEmpty(conditionalItem);
                });
                return invalidConditionalItemIndex >= 0;
            } else {
                return isFormItemTypeRequirable(item.type) && item.required && isFormItemValueEmpty(item);
            }
        });

        return invalidItemIndex === -1 && getSectionsMissingAnswers(formContent.content).length === 0;
    }
});

export const formQuestionsState = atom<FormTemplate | undefined>({ key: "form.template.questions", default: undefined });

export const selectedFormSubmissionState = atom<FormTemplate | undefined>({
    key: "form.submission.selected",
    default: undefined
});

export const formSubmissionsState = atom({
    key: "formsubmissions",
    default: [] as FormTemplate[],
});

export const voizReportsState = atom({
    key: "voizreports",
    default: [] as VoizReportSubmission[],
});

export const voizReportsTotalState = atom({
    key: "voizreports.total",
    default: 0,
});

export const formSubmissionsByTemplateState = selectorFamily<FormTemplate[], FormContentIds>({
    key: "formsubmissions.bytemplate",
    get: (ids) => ({ get }) => get(formSubmissionsState)
        .filter(f => f.id === ids.templateId || f.content.title === ids.title),
});

export const formSubmissionsForJobState = selectorFamily<FormTemplate[], {submissionId: string, templateId: string}[]>({
    key: "formsubmissions.forjob",
    get: (submittedForms) => ({ get }) => {
        const formSubmissions = get(formSubmissionsState);
        const jobFormSubmissions = submittedForms
            .map((submittedForm) => {
                return formSubmissions.find(f => f.id === submittedForm.templateId && f.submissionUid === submittedForm.submissionId)
            })
            .filter((submission) => submission !== undefined) as FormTemplate[];
        return jobFormSubmissions.sort((f1, f2) => f2.createdTs - f1.createdTs);
    },
});

export const standardFormContentState = selector<FormContent>({
    key: "form.standard",
    get: ({ get }) => {
        const standardForm = get(companyFieldState("standard_form"));
        return standardForm || { content: [] as FormItem[] } as FormContent;
    }
});

export const editStandardFormContentState = atom<FormContent>({
    key: "edit.form.standard",
    default: standardFormContentState,
});

/** Bumped to open the form editor on a new form from outside the list (the empty Forms page). */
export const formCreateRequestState = atom<number>({
    key: "forms.createRequest",
    default: 0,
});
